const SELLING_PLAN_FIELD_NAME = 'selling_plan';
const STORAGE_KEY = `selectedSellingPlan_${window.location.hostname}`;
const PLAN_CHANGE_EVENT = 'planChange';
const LEGACY_WIDGET_SELECTOR = '#appstle_subscription_widget0';

// Bridge inline change handlers with the custom element event flow.
function handleSellingPlanChange(event) {
  const component = event.target.closest('selling-plans');
  if (component) {
    // Update Appstle radio buttons based on the selected plan type
    const radio = event.target;
    if (radio && radio.dataset.radio) {
      const appstleSubscriptionRadio = document.querySelector('.appstle_subscription_radio_wrapper input[type="radio"]');
      const appstleOneTimeRadio = document.querySelector('.appstle_one_time_details_wrapper input[type="radio"]');

      if (radio.dataset.radio === 'subscriptions') {
        // Subscription is selected
        if (appstleSubscriptionRadio) appstleSubscriptionRadio.checked = true;
        if (appstleOneTimeRadio) appstleOneTimeRadio.checked = false;
      } else if (radio.dataset.radio === 'one-time') {
        // One-time purchase is selected
        if (appstleSubscriptionRadio) appstleSubscriptionRadio.checked = false;
        if (appstleOneTimeRadio) appstleOneTimeRadio.checked = true;
      }
    }

    component.dispatchEvent(new CustomEvent(PLAN_CHANGE_EVENT));
  }
}

// Handle subscription-specific changes within the subscriptions tab
function handleSubscriptionChange(event) {
  const component = event.target.closest('selling-plans');
  if (!component) {
    console.warn('Selling plans component not found');
    return;
  }

  const subscriptionInput = event.target;
  if (!subscriptionInput) {
    console.warn('Subscription input not found');
    return;
  }

  const subscriptionId = subscriptionInput.dataset.subscriptionId;
  const variantId = subscriptionInput.dataset.variantId;
  const normalPrice = subscriptionInput.dataset.normalPrice;
  const compareAtPrice = subscriptionInput.dataset.compareAtPrice;
  const description = subscriptionInput.dataset.description || '';

  if (!subscriptionId || !variantId) {
    console.warn('Missing subscription data:', { subscriptionId, variantId });
    return;
  }

  // Find and check the main subscription radio button
  const mainSubscriptionRadio = component.querySelector('[data-radio="subscriptions"]');
  if (mainSubscriptionRadio) {
    mainSubscriptionRadio.checked = true;
    // Update the main radio's data attributes to match the selected subscription
    mainSubscriptionRadio.dataset.subscription = subscriptionId;
    mainSubscriptionRadio.dataset.variantId = variantId;
    mainSubscriptionRadio.dataset.normalPrice = normalPrice;
    mainSubscriptionRadio.dataset.compareAtPrice = compareAtPrice || '';
    console.log('mainSubscriptionRadio checked');
  } else {
    console.warn('Main subscription radio not found');
  }

  // Update the description if it exists
  const descriptionElement = component.querySelector('[data-subscription-description]');
  if (descriptionElement) {
    if (description) {
      descriptionElement.innerHTML = description;
      descriptionElement.style.display = 'block';
    } else {
      descriptionElement.style.display = 'none';
    }
  }

  // Trigger the plan change handler to update form and prices
  component.dispatchEvent(new CustomEvent(PLAN_CHANGE_EVENT));
}

// Make function globally accessible
window.handleSubscriptionChange = handleSubscriptionChange;

if (!customElements.get('selling-plans')) {
  class SellingPlans extends HTMLElement {
    constructor() {
      super();
      this.handlePlanChange = this.handlePlanChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.variantChangeUnsubscriber = undefined;
    }

    connectedCallback() {
      this.cacheDomReferences();
      this.addEventListener(PLAN_CHANGE_EVENT, this.handlePlanChange);
      this.attachSubscriptionListeners();
      this.initializeSubscriptions();
      this.restoreSelection();
      this.subscribeToVariantChanges();
      this.syncAppstleRadiosOnLoad();
    }

    subscribeToVariantChanges() {
      // Subscribe to variant change events to update selling plan options
      // subscribe and PUB_SUB_EVENTS should be available globally from pubsub.js and constants.js
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }
    }

    handleVariantChange({ data: { variant } }) {
      if (!variant) return;

      // Update the one-time purchase radio button with the new variant's data
      const oneTimeRadio = this.querySelector('[data-radio="one-time"]');
      if (oneTimeRadio) {
        // Format prices without currency symbol for data attributes (German format: comma as decimal separator)
        const normalPrice = this.formatPriceForData(variant.price);
        const compareAtPrice = variant.compare_at_price ? this.formatPriceForData(variant.compare_at_price) : '';

        oneTimeRadio.dataset.variantId = variant.id.toString();
        oneTimeRadio.dataset.normalPrice = normalPrice;
        oneTimeRadio.dataset.compareAtPrice = compareAtPrice;
        console.log('one-time radio checked');
        document.querySelector('.appstle_subscription_radio_wrapper input[type="radio"]').checked = false;
        document.querySelector('.appstle_one_time_details_wrapper input[type="radio"]').checked = true;

        // Update aria-label with formatted price
        const priceFormatted = this.formatPriceWithCurrency(variant.price);
        oneTimeRadio.setAttribute('aria-label', `One-time purchase. Product price ${priceFormatted}`);

        // If one-time is currently selected, update the price display
        if (oneTimeRadio.checked) {

          this.handlePlanChange();
        }
      }
    }

    formatPriceForData(price) {
      // Convert price from cents to formatted string without currency
      // Shopify variant.price is in cents, format as German locale (comma as decimal separator)
      const priceInCurrency = price / 100;
      // Format with comma as decimal separator (e.g., "21,95")
      return priceInCurrency.toFixed(2).replace('.', ',');
    }

    formatPriceWithCurrency(price) {
      // Convert price from cents to formatted string with currency (e.g., "21,95€")
      const priceInCurrency = price / 100;
      return `${priceInCurrency.toFixed(2).replace('.', ',')}€`;
    }

    attachSubscriptionListeners() {
      // Attach event listeners to all subscription radio inputs
      // Use a more specific selector to target subscription inputs
      const subscriptionContainer = this.querySelector('.selling-plans__subscriptions');
      if (!subscriptionContainer) return;

      const subscriptionInputs = subscriptionContainer.querySelectorAll('input[type="radio"]');
      subscriptionInputs.forEach(input => {
        // Use both change and click events to ensure it works
        input.addEventListener('change', (event) => {
          handleSubscriptionChange(event);
        });
        input.addEventListener('click', (event) => {
          // Ensure the input is checked when clicked directly
          if (!event.target.checked) {
            event.target.checked = true;
            event.target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    initializeSubscriptions() {
      // Find the checked subscription input and sync it with the main radio
      const checkedSubscription = this.querySelector('.selling-plans__input:checked');
      if (checkedSubscription) {
        const subscriptionId = checkedSubscription.dataset.subscriptionId;
        const variantId = checkedSubscription.dataset.variantId;
        const normalPrice = checkedSubscription.dataset.normalPrice;
        const compareAtPrice = checkedSubscription.dataset.compareAtPrice;
        const description = checkedSubscription.dataset.description || '';

        const mainSubscriptionRadio = this.querySelector('[data-radio="subscriptions"]');
        if (mainSubscriptionRadio) {
          mainSubscriptionRadio.dataset.subscription = subscriptionId;
          mainSubscriptionRadio.dataset.variantId = variantId;
          mainSubscriptionRadio.dataset.normalPrice = normalPrice;
          mainSubscriptionRadio.dataset.compareAtPrice = compareAtPrice || '';
        }

        // Update the description if it exists
        const descriptionElement = this.querySelector('[data-subscription-description]');
        if (descriptionElement && description) {
          descriptionElement.innerHTML = description;
          descriptionElement.style.display = 'block';
        }
      }
    }

    syncAppstleRadiosOnLoad() {
      // Sync Appstle radios based on which radio is checked on page load
      // Show loading state on buttons while waiting for Appstle widget to load
      const buttons = this.form?.querySelectorAll('button[type="submit"], .product-form__submit');
      if (buttons && buttons.length) {
        buttons.forEach(button => this.showLoadingState(button));
      }

      const checkedRadio = this.querySelector('[data-radio]:checked');
      if (!checkedRadio) {
        // Hide loading state even if no radio is checked
        if (buttons && buttons.length) {
          buttons.forEach(button => this.hideLoadingState(button));
        }
        return;
      }

      // Function to sync Appstle radios
      const syncRadios = () => {
        const appstleSubscriptionRadio = document.querySelector('.appstle_subscription_radio_wrapper input[type="radio"]');
        const appstleOneTimeRadio = document.querySelector('.appstle_one_time_details_wrapper input[type="radio"]');

        if (!appstleSubscriptionRadio || !appstleOneTimeRadio) {
          return false; // Radios not found yet
        }

        if (checkedRadio.dataset.radio === 'subscriptions') {
          appstleSubscriptionRadio.checked = true;
          appstleOneTimeRadio.checked = false;
          console.log('Page load: subscription radio synced');
        } else if (checkedRadio.dataset.radio === 'one-time') {
          appstleSubscriptionRadio.checked = false;
          appstleOneTimeRadio.checked = true;
          console.log('Page load: one-time radio synced');
        }

        return true; // Synced successfully
      };

      // Try to sync immediately in case Appstle is already loaded
      if (syncRadios()) {
        if (buttons && buttons.length) {
          buttons.forEach(button => this.hideLoadingState(button));
        }
        return;
      }

      // If not available yet, use MutationObserver to watch for Appstle widget
      const observer = new MutationObserver((mutations, obs) => {
        if (syncRadios()) {
          // Stop observing once synced
          obs.disconnect();
          if (buttons && buttons.length) {
            buttons.forEach(button => this.hideLoadingState(button));
          }
        }
      });

      // Start observing the document body for added nodes
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Fallback timeout to stop waiting after 5 seconds
      setTimeout(() => {
        observer.disconnect();
        if (!syncRadios()) {
          console.warn('Appstle radio buttons not found after timeout');
        }
        // Hide loading state regardless
        if (buttons && buttons.length) {
          buttons.forEach(button => this.hideLoadingState(button));
        }
      }, 5000);
    }

    // Shows the loading state on the button
    showLoadingState(button) {
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) {
        spinner.classList.remove('hidden');
      }
    }

    // Hides the loading state on the button
    hideLoadingState(button) {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) {
        spinner.classList.add('hidden');
      }
    }

    disconnectedCallback() {
      this.removeEventListener(PLAN_CHANGE_EVENT, this.handlePlanChange);
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
      if (this.actButton) {
        // this.actButton.removeEventListener('click', this.handleActButtonClick);
      }
    }

    cacheDomReferences() {
      this.radios = this.querySelectorAll('[data-radio]');
      this.tabs = this.querySelectorAll('[data-tab]');
      this.productInfo = this.productInfo || this.closest('product-info') || document;
      const scope = this.productInfo || document;
      this.form = scope.querySelector('[data-type="add-to-cart-form"]') || document.querySelector('[data-type="add-to-cart-form"]');
      this.productVariantId = this.form?.querySelector('.product-variant-id') || null;
      this.regularPrice = scope.querySelector('[data-regular-price]') || null;
      this.salePrice = scope.querySelector('[data-sale-price]') || null;
      this.priceBadges = scope.querySelectorAll('[data-price-badge]') || null;
    }

    handlePlanChange() {
      this.cacheDomReferences();
      this.radios.forEach((radio) => {
        if (!radio.checked) return;
        this.persistSelection(radio);
        this.toggleTabs(radio);
        this.syncVariant(radio);
        this.updatePriceDisplay(radio);
      });
    }

    persistSelection(radio) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ type: radio.dataset.radio }));
    }

    toggleTabs(radio) {
      if (!this.tabs.length) return;
      this.tabs.forEach((tab) => tab.removeAttribute('data-open'));
      const targetTab = this.querySelector(`[data-tab="${radio.dataset.radio}"]`);
      if (targetTab) {
        targetTab.setAttribute('data-open', '');
      }
    }

    syncVariant(radio) {
      if (!this.form || !this.productVariantId) return;

      if (radio.dataset.radio === 'subscriptions') {
        const input = this.getOrCreateSellingPlanInput();
        input.value = radio.dataset.subscription;
        input.setAttribute('data-selected-selling-plan', radio.dataset.subscription);
      } else {
        const existingInput = this.form.querySelector(`input[name="${SELLING_PLAN_FIELD_NAME}"]`);
        if (existingInput) {
          existingInput.remove();
        }
      }

      this.productVariantId.value = radio.dataset.variantId;
    }

    getOrCreateSellingPlanInput() {
      let input = this.form.querySelector(`input[name="${SELLING_PLAN_FIELD_NAME}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = SELLING_PLAN_FIELD_NAME;
        this.form.prepend(input);
      }
      return input;
    }

    restoreSelection() {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
          const { type } = JSON.parse(saved) || {};
          const radio = type ? this.querySelector(`[data-radio="${type}"]`) : null;
          if (radio) {
            radio.checked = true;
          }
        }
      } catch (error) {
        console.warn('Failed to restore selling plan selection:', error);
      }
      this.handlePlanChange();
    }

    // Swap between sale and regular price states based on the selected plan metadata.
    updatePriceDisplay(radio) {
      const scope = this.productInfo || document;
      this.regularPrice = this.regularPrice || scope.querySelector('[data-regular-price]');
      this.salePrice = this.salePrice || scope.querySelector('[data-sale-price]');

      if (!this.regularPrice && !this.salePrice) return;

      const normalPriceRaw = radio.dataset.normalPrice;
      const compareAtPriceRaw = radio.dataset.compareAtPrice || radio.dataset.compareAtPrice;
      const normalPrice =
        typeof normalPriceRaw === 'string' ? normalPriceRaw.trim() : normalPriceRaw;
      const compareAtPrice =
        typeof compareAtPriceRaw === 'string' ? compareAtPriceRaw.trim() : compareAtPriceRaw;
      if (!normalPrice) return;

      const hasCompareAtPrice =
        Boolean(compareAtPrice) && compareAtPrice !== '' && compareAtPrice !== normalPrice;

      if (this.regularPrice) {
        this.regularPrice.dataset.regularPrice = !hasCompareAtPrice;
      }
      if (this.salePrice) {
        this.salePrice.dataset.salePrice = hasCompareAtPrice;
      }

      // Toggle parent .price--on-sale so CSS shows sale block and badges (e.g. .price--on-sale .price__badge-sale)
      const priceEl = (this.salePrice || this.regularPrice).closest('.price');
      if (priceEl) {
        if (hasCompareAtPrice) {
          priceEl.classList.add('price--on-sale');
        } else {
          priceEl.classList.remove('price--on-sale');
        }
      }

      if (!hasCompareAtPrice) {
        this.renderRegularPrice(normalPrice);
        return;
      }

      this.renderSalePrice(normalPrice, compareAtPrice);
    }

    renderRegularPrice(value) {
      if (!this.regularPrice) return;
      this.clearElement(this.regularPrice);
      this.regularPrice.appendChild(
        this.createPriceNode(value, {
          tag: 'span',
          className: 'price-item price-item--regular'
        })
      );

      // Adjust price badges margin based on width difference
      if (this.priceBadges && this.priceBadges.length) {
        requestAnimationFrame(() => {
          const wrapperWidth = this.regularPrice.parentElement?.offsetWidth || 0;
          const priceItemWidth = this.regularPrice.offsetWidth || 0;
          const offset = wrapperWidth - priceItemWidth;
          this.priceBadges.forEach(badge => {
            badge.style.marginLeft = `-${offset}px`;
          });
        });
      }
    }

    renderSalePrice(normalPrice, compareAtPrice) {
      if (!this.salePrice) return;

      const priceWithBadge = this.salePrice.querySelector('.price-with-badge');
      if (priceWithBadge) {
        this.updateSalePriceInPlace(normalPrice, compareAtPrice);
        return;
      }

      this.clearElement(this.salePrice);

      const scope = this.productInfo || document;
      const priceEl = this.salePrice.closest('.price') || scope.querySelector('.price');
      const badgeLabel = priceEl?.dataset?.priceBadgeLabel;
      const hasBadgeData = Boolean(badgeLabel);

      const compareWrapper = document.createElement('span');
      compareWrapper.appendChild(
        this.createPriceNode(compareAtPrice, {
          tag: 's',
          className: 'price-item price-item--regular'
        })
      );
      this.salePrice.appendChild(compareWrapper);

      if (hasBadgeData) {
        const normalNum = this.parsePriceString(normalPrice);
        const compareNum = this.parsePriceString(compareAtPrice);
        const savings = Math.max(0, compareNum - normalNum);
        const savingsDisplay = this.formatSavingsPrice(savings);

        const badgeBg = priceEl.dataset.priceBadgeBg || '';
        const badgeColor = priceEl.dataset.priceBadgeColor || '';
        const badgeRadius = priceEl.dataset.priceBadgeRadius ?? '0';
        const soldOutText = priceEl.dataset.soldOutText || '';
        const soldOutClass = priceEl.dataset.soldOutBadgeClass || 'badge price__badge-sold-out';

        const priceWithBadgeDiv = document.createElement('div');
        priceWithBadgeDiv.className = 'price-with-badge';

        const saleSpan = this.createPriceNode(normalPrice, {
          tag: 'span',
          className: 'price-item price-item--sale price-item--last'
        });
        priceWithBadgeDiv.appendChild(saleSpan);

        const badgeSpan = document.createElement('span');
        badgeSpan.className = 'badge price__badge-sale';
        badgeSpan.setAttribute(
          'style',
          `background-color: ${badgeBg}; color: ${badgeColor}; border-radius: ${badgeRadius}px;`
        );
        badgeSpan.dataset.badgeLabel = badgeLabel;
        badgeSpan.textContent = `${badgeLabel} ${savingsDisplay}`;
        priceWithBadgeDiv.appendChild(badgeSpan);

        const soldOutSpan = document.createElement('span');
        soldOutSpan.className = soldOutClass;
        soldOutSpan.textContent = soldOutText;
        priceWithBadgeDiv.appendChild(soldOutSpan);

        this.salePrice.appendChild(priceWithBadgeDiv);
      } else {
        this.salePrice.appendChild(
          this.createPriceNode(normalPrice, {
            tag: 'span',
            className: 'price-item price-item--sale price-item--last'
          })
        );
      }

      // Reset price badges margin
      if (this.priceBadges && this.priceBadges.length) {
        this.priceBadges.forEach(badge => {
          badge.style.marginLeft = '0';
        });
      }
    }

    // Parse German-style price string (e.g. "69,99") to number
    parsePriceString(str) {
      if (str == null || str === '') return 0;
      const normalized = String(str).trim().replace(',', '.');
      return parseFloat(normalized) || 0;
    }

    // Format number as German price string with currency (e.g. "49,26€")
    formatSavingsPrice(value) {
      const num = Number(value);
      if (isNaN(num) || num <= 0) return '0,00€';
      return `${num.toFixed(2).replace('.', ',')}€`;
    }

    updateSalePriceInPlace(normalPrice, compareAtPrice) {
      const compareNode = this.salePrice.querySelector('s.price-item--regular');
      const saleNode = this.salePrice.querySelector('.price-item--sale.price-item--last');
      const badgeNode = this.salePrice.querySelector('.price__badge-sale');

      const normalNum = this.parsePriceString(normalPrice);
      const compareNum = this.parsePriceString(compareAtPrice);
      const savings = Math.max(0, compareNum - normalNum);
      const displayPrice = this.formatSavingsPrice(normalNum);
      const compareDisplay = this.formatSavingsPrice(compareNum);
      const savingsDisplay = this.formatSavingsPrice(savings);

      if (compareNode) {
        compareNode.textContent = compareDisplay;
      }
      if (saleNode) {
        saleNode.textContent = displayPrice;
      }
      if (badgeNode) {
        const label = badgeNode.dataset.badgeLabel || '';
        badgeNode.textContent = label ? `${label} ${savingsDisplay}` : savingsDisplay;
      }

      if (this.priceBadges && this.priceBadges.length) {
        this.priceBadges.forEach(badge => {
          badge.style.marginLeft = '0';
        });
      }
    }

    createPriceNode(value, { tag, className }) {
      const node = document.createElement(tag);
      if (className) {
        node.className = className;
      }
      // Ensure a trailing currency symbol for prices coming from dataset values
      // (which are stored without currency, e.g. "18,66").
      const hasEuro = typeof value === 'string' && value.trim().endsWith('€');
      const displayValue = hasEuro ? value.trim() : `${value}€`;
      node.textContent = displayValue;
      return node;
    }

    clearElement(element) {
      while (element && element.firstChild) {
        element.innerHTML = '';
      }
    }

  }

  customElements.define('selling-plans', SellingPlans);
}
